/**
 * Redesigned search core (Search Alpha/Beta A/B test), used by the header search and the search results
 * page's own search box. Alpha and Beta both load it; each wrapper's `search-alpha-results-section` attribute
 * picks the dropdown section (predictive-search-alpha or predictive-search-beta). Beta's extras are in search-beta.js.
 *
 * Only loaded when the theme setting `search_variant` is 'alpha' or 'beta' (see layout/theme.liquid).
 *
 * main.js fetches the original predictive search on `keyup` of both inputs, with products only. This design
 * needs products and articles, so each `keyup` is stopped before main.js and the variant's dropdown section is
 * rendered instead.
 *
 * The dropdown is rendered on the search results page URLs (the ones "View all results" / "View all blogs" link
 * to and Enter submits), not /search/suggest: predictive search ranks differently, so its top products didn't
 * match the results page. Products, brands and the count come from the products search; "From the blog" comes
 * from the blogs search, fetched alongside and dropped into the products response's [search-alpha-articles-slot].
 */

/**
 * Product searches complete the shopper's unfinished last word. Shopify's product search doesn't prefix-match
 * short words: "emb" finds 7 products while "embryolisse" finds 51 ("cau" 5 vs "caudalie" 54). Predictive search
 * does know the word (it suggests "embryolisse" for "emb"), so a product search runs with that word instead, and
 * the results page shows it in the box. Words that are already whole ("lip", "rose") are left alone.
 * Only product searches: the blog search already prefix-matches ("emb" finds its 151 posts).
 */
const searchAlphaQuery = (() => {
  const rootUrl = window.Shopify?.routes?.root || '/';
  const pending = new Map();
  const resolved = new Map();

  const keyOf = (query) => query.trim().toLowerCase();

  /**
   * @param {string} query - the shopper's words
   * @returns {Promise<string>} the query with its last word completed, or unchanged when it's already a whole word
   *   or nothing completes it
   */
  const expand = (query) => {
    const term = query.trim();
    const words = term.split(/\s+/);
    const last = words[words.length - 1] || '';
    if (last.length < 2 || /[():"]/.test(term)) return Promise.resolve(term);

    const key = keyOf(term);
    if (!pending.has(key)) {
      const params = new URLSearchParams({ q: term, 'resources[type]': 'query,product', 'resources[limit]': '6' });
      pending.set(
        key,
        fetch(`${rootUrl}search/suggest.json?${params.toString()}`)
          .then((response) => (response.ok ? response.json() : Promise.reject(new Error(response.status))))
          .then((data) => {
            const lastWord = last.toLowerCase();
            // Suggested searches first, then the suggested products' brands and titles (e.g. "cau" has no
            // suggested search but its products are by Caudalie)
            const { queries = [], products = [] } = data.resources?.results || {};
            const suggestedWords = [
              ...queries.map((suggestion) => suggestion.text),
              ...products.map((product) => product.vendor || ''),
              ...products.map((product) => product.title || ''),
            ].flatMap((text) => text.toLowerCase().split(/[^\p{L}\p{N}]+/u).filter(Boolean));
            if (suggestedWords.includes(lastWord)) return term;

            const completion = suggestedWords.find((word) => word.length > lastWord.length && word.startsWith(lastWord));
            return completion ? [...words.slice(0, -1), completion].join(' ') : term;
          })
          .catch(() => term)
          .then((expanded) => {
            resolved.set(key, expanded);
            return expanded;
          })
      );
    }
    return pending.get(key);
  };

  /** @returns {string|undefined} the completed query if it's already known, without waiting */
  const expandedNow = (query) => resolved.get(keyOf(query));

  /** @returns {Promise<string>} a product search link with its query completed (other links unchanged) */
  const expandUrl = (href) => {
    const url = new URL(href, window.location.origin);
    const query = url.searchParams.get('q') || '';
    if (url.searchParams.get('type') !== 'product' || !query) return Promise.resolve(href);

    return expand(query).then((expanded) => {
      url.searchParams.set('q', expanded);
      return `${url.pathname}${url.search}${url.hash}`;
    });
  };

  return { expand, expandedNow, expandUrl };
})();

/**
 * The discount app (tdf) shows sale prices by rewriting `.product-card__price`, but only for products it hasn't
 * seen yet, so cards rendered again (every keystroke, tab switch) fall back to the full price. It re-labels the
 * page on a `reload_colection` event (sic, the name its store script listens for), so fire that after rendering.
 */
const searchAlphaRefreshSalePrices = () => document.dispatchEvent(new CustomEvent('reload_colection'));

(function () {
  const MIN_TERM_LENGTH = 3;
  const DEBOUNCE_MS = 250;
  const rootUrl = window.Shopify?.routes?.root || '/';

  const sectionHtml = (html, sectionId) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.querySelector(`#shopify-section-${sectionId}`);
  };

  const fetchText = (url, signal) =>
    fetch(url, { signal }).then((response) => {
      if (!response.ok) throw new Error(response.status);
      return response.text();
    });

  // Same query as the results page tabs (snippets/search-alpha-tabs.liquid) and the header form's hidden fields
  const resultsUrl = (term, type, sectionId) => {
    const params = new URLSearchParams({ q: term, type, 'options[prefix]': 'last', section_id: sectionId });
    return `${rootUrl}search?${params.toString()}`;
  };

  /**
   * Fires on the search form so variant add-ons can react: `search-alpha:empty` when the field is cleared or
   * focused with fewer than MIN_TERM_LENGTH characters (Beta shows its history/trending panel), and
   * `search-alpha:results` after results render.
   *
   * @param {HTMLInputElement} searchInput - the search field
   * @param {string} clearSelector - buttons that close/clear this search
   * @param {string} sectionId - dropdown section to render (predictive-search-alpha | predictive-search-beta)
   * @param {boolean} keepTermOnClose - the clearSelector buttons only close the search, keeping the words
   */
  const initAlphaSearch = (searchInput, clearSelector, sectionId, keepTermOnClose = false) => {
    const searchForm = searchInput?.closest('form');
    const resultsContainer = searchForm?.querySelector('.predictive-search');
    if (!searchForm || !resultsContainer) return;

    const announce = (name) => searchForm.dispatchEvent(new CustomEvent(name, { bubbles: true }));

    let debounceTimer = null;
    let abortController = null;
    // The last results shown, so closing and reopening the search brings them back (as the original search does)
    let lastResults = null;

    const clearResults = () => {
      abortController?.abort();
      resultsContainer.innerHTML = '';
    };

    const showResults = (term, html) => {
      lastResults = { term, html };
      resultsContainer.innerHTML = html;
      announce('search-alpha:results');

      // Same refresh the original dropdown runs so card-product wishlist/back-in-stock buttons render.
      // Quick view and CartX add-to-cart use delegated listeners and need nothing here.
      window.$th_refreshWishlistItems?.();
      window.$th_refreshBackInStockItems?.();
      searchAlphaRefreshSalePrices();
    };

    // Puts the blogs search's "From the blog" group in the products response's slot. With no products and some
    // blogs (Beta), the blogs replace its "no results" message.
    const addArticles = (section, articlesSection) => {
      const slot = section.querySelector('[search-alpha-articles-slot]');
      const articles = articlesSection?.querySelector('[search-alpha-articles]');
      if (!slot) return;

      if (!articles) {
        slot.remove();
        return;
      }

      slot.replaceWith(...articles.childNodes);
      section.querySelector('[search-alpha-no-results]')?.remove();
    };

    const search = (term) => {
      abortController?.abort();
      abortController = new AbortController();
      const { signal } = abortController;

      Promise.all([
        searchAlphaQuery.expand(term).then((productQuery) => fetchText(resultsUrl(productQuery, 'product', sectionId), signal)),
        fetchText(resultsUrl(term, 'article', sectionId), signal),
      ])
        .then(([productsHtml, articlesHtml]) => {
          const section = sectionHtml(productsHtml, sectionId);
          if (!section || searchInput.value.trim() !== term) return;

          addArticles(section, sectionHtml(articlesHtml, sectionId));
          // The heading names the shopper's own words, not the completed query ("cau", not "caudalie")
          section.querySelectorAll('[search-alpha-results-term]').forEach((heading) => {
            heading.textContent = term;
          });
          showResults(term, section.innerHTML);
        })
        .catch((error) => {
          if (error.name !== 'AbortError') console.error('[search-alpha]', error);
        });
    };

    // Phones: scrolling the results (or Beta's recent/trending panel, which sits beside the form in the same
    // wrapper) closes the keyboard, so the list isn't half hidden behind it. Typing again reopens it.
    const searchArea = searchInput.closest('[search-alpha], .search-alpha-page-search') || searchForm;
    searchArea.addEventListener(
      'touchmove',
      (event) => {
        if (document.activeElement !== searchInput || event.target === searchInput) return;
        searchInput.blur();
      },
      { passive: true }
    );

    // Stop main.js's original predictive search (bound to `keyup` on the input) from running.
    searchForm.addEventListener(
      'keyup',
      (event) => {
        if (event.target === searchInput) event.stopPropagation();
      },
      true
    );

    searchInput.addEventListener('input', () => {
      const term = searchInput.value.trim();
      clearTimeout(debounceTimer);

      if (term.length < MIN_TERM_LENGTH) {
        clearResults();
        announce('search-alpha:empty');
        return;
      }

      debounceTimer = setTimeout(() => search(term), DEBOUNCE_MS);
    });

    // Capture phase so this runs before header-form-custom.js stops the click. Closing the header search keeps the
    // words (its X closes the panel, as in the original search); the search page's X clears them.
    document.addEventListener(
      'click',
      (event) => {
        if (!(event.target instanceof Element) || !event.target.closest(clearSelector)) return;
        clearTimeout(debounceTimer);
        clearResults();
        if (!keepTermOnClose) {
          searchInput.value = '';
          lastResults = null;
        }
      },
      true
    );

    // Clicking anywhere outside the search box closes its dropdown (the header search icon opens it, so it's not "outside").
    // Quick view opened from a result, and the cart drawer it adds to, are part of the same journey: clicks inside
    // them (quantity, shade, add to cart) leave the results open so the shopper can carry on from the same search.
    const KEEP_OPEN_SELECTOR = '[header-search-toggle], #custom-overlay, #search-overlay, [class*="cartx-drawer"]';
    document.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      if (searchForm.contains(event.target) || event.target.closest(KEEP_OPEN_SELECTOR)) return;
      clearResults();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') clearResults();
    });

    const restoreResults = () => {
      const term = searchInput.value.trim();
      if (lastResults?.term === term && !resultsContainer.hasChildNodes()) showResults(term, lastResults.html);
    };

    // Reopening the search (or clicking back into the field) shows the last results again for the same words.
    searchInput.addEventListener('focus', () => {
      if (searchInput.value.trim().length < MIN_TERM_LENGTH) {
        announce('search-alpha:empty');
        return;
      }
      restoreResults();
    });

    // The header toggle flips aria-expanded; the field may still have focus then, so focus alone isn't enough.
    if (searchArea.hasAttribute('aria-expanded')) {
      new MutationObserver(() => {
        if (searchArea.getAttribute('aria-expanded') === 'true') restoreResults();
      }).observe(searchArea, { attributes: true, attributeFilter: ['aria-expanded'] });
    }
  };

  // Enter searches the completed query, the one the dropdown showed. When it's known (the dropdown fetched it while
  // the shopper typed) it goes in as the submitted `q`, under the visible field, so the normal submit carries on
  // (main.js, search tracking). Otherwise the submit waits for it and runs again.
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.closest('[search-alpha], .search-alpha-page-search')) return;

      const input = form.querySelector('input[name="q"], input[data-search-alpha-name="q"]');
      const type = form.querySelector('input[name="type"]')?.value;
      const query = input?.value.trim();
      if (!query || type !== 'product') return;

      const expanded = searchAlphaQuery.expandedNow(query);
      if (expanded === undefined) {
        event.preventDefault();
        event.stopImmediatePropagation();
        searchAlphaQuery.expand(query).then(() => form.requestSubmit());
        return;
      }

      form.querySelector('[search-alpha-submitted-query]')?.remove();
      if (expanded === query) {
        input.name = 'q';
        return;
      }

      const submitted = document.createElement('input');
      submitted.type = 'hidden';
      submitted.name = 'q';
      submitted.value = expanded;
      submitted.setAttribute('search-alpha-submitted-query', '');
      input.dataset.searchAlphaName = 'q';
      input.removeAttribute('name');
      input.after(submitted);
    },
    true
  );

  // Coming back to a page from the back/forward cache, put the field back as the form's `q`
  window.addEventListener('pageshow', () => {
    document.querySelectorAll('[search-alpha-submitted-query]').forEach((submitted) => submitted.remove());
    document.querySelectorAll('input[data-search-alpha-name="q"]').forEach((input) => {
      input.name = 'q';
    });
  });

  const resultsSectionFor = (wrapper) =>
    wrapper.getAttribute('search-alpha-results-section') || 'predictive-search-alpha';

  const headerSearch = document.querySelector('[search-alpha]');
  if (headerSearch) {
    initAlphaSearch(
      headerSearch.querySelector('[header-search-form-input]'),
      '[header-search-close-btn], .search-close-btn',
      resultsSectionFor(headerSearch),
      true
    );
  }

  const pageSearch = document.querySelector('.search-alpha-page-search');
  if (pageSearch) {
    initAlphaSearch(
      pageSearch.querySelector('[search-section-form-input]'),
      '[search-alpha-page-clear]',
      resultsSectionFor(pageSearch)
    );
  }
})();

/**
 * Search results page tabs (snippets/search-alpha-tabs.liquid): the active tab's count is rendered
 * by Liquid, the other tab's count is fetched from sections/search-alpha-count.liquid on its own URL.
 */
(function () {
  const COUNT_SECTION_ID = 'search-alpha-count';

  document.querySelectorAll('[search-alpha-tabs] [search-alpha-tab-count]').forEach((countTarget) => {
    const tab = countTarget.closest('a');
    if (!tab) return;

    // From the Blogs tab, the Products tab searches the completed query (see searchAlphaQuery)
    searchAlphaQuery
      .expandUrl(tab.getAttribute('href'))
      .then((href) => {
        tab.setAttribute('href', href);
        return fetch(`${href}&section_id=${COUNT_SECTION_ID}`);
      })
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const countValue = doc.querySelector(`#shopify-section-${COUNT_SECTION_ID} [search-alpha-count-value]`);
        if (countValue) countTarget.textContent = `(${parseInt(countValue.textContent, 10) || 0})`;
      })
      .catch((error) => console.error('[search-alpha]', error));
  });
})();

/**
 * Search results page Products / Blogs tabs switch instantly, without a page load.
 *
 * Everything under the search box is one tab's view (sections/main-search.liquid, [search-alpha-view]). Once the
 * page is up, the other tab's view is fetched in the background through the Section Rendering API and kept hidden;
 * a tap just swaps which view shows and updates the URL. Switching back shows the original view, still wired up
 * (filters, sort, quick view). Back/forward swap too. If the other view isn't ready or can't be fetched, the tab
 * falls back to a normal page load.
 *
 * Taps act on pointerup: with the keyboard open, iOS spends the first tap closing it and the click is lost.
 */
(function () {
  const firstView = document.querySelector('[search-alpha-view]');
  if (!firstView || !firstView.querySelector('[search-alpha-tabs]')) return;

  const sectionId = firstView.closest('.shopify-section')?.id.replace('shopify-section-', '');
  const TAB_SELECTOR = '[search-alpha-tabs] .search-alpha-tabs__tab';
  const ACTIVE_CLASS = 'search-alpha-tabs__tab--active';
  const SWITCHING_CLASS = 'search-alpha-switching';
  const TAP_TOLERANCE = 10;
  // Products controls main.js wires up once on load; a fetched Products view has to reload to use them
  const PAGE_LOAD_CONTROLS = '[collection-sortings-toggle], [collection-sortings-filter-option], [collection-sortings-sort-option], [collection-sortings-filter-close], [collection-sortings-sort-close]';

  const tabOf = (url) => (new URL(url, window.location.origin).searchParams.get('type') === 'article' ? 'article' : 'product');
  const views = new Map([[firstView.dataset.searchTab, firstView]]);
  const pending = new Map();

  // Each view's own tab bar only knows its own count; fill the other tab's from the view that has it
  const shareCounts = () => {
    views.forEach((view) => {
      view.querySelectorAll(TAB_SELECTOR).forEach((tab) => {
        const countTarget = tab.querySelector('[search-alpha-tab-count]');
        const source = views.get(tabOf(tab.href))?.querySelector(`.${ACTIVE_CLASS} .search-alpha-tabs__count`);
        if (countTarget && !countTarget.textContent && source?.textContent) countTarget.textContent = source.textContent;
      });
    });
  };

  const load = (url) => {
    const tab = tabOf(url);
    if (views.has(tab)) return Promise.resolve(views.get(tab));
    if (!sectionId) return Promise.reject(new Error('no section id'));
    if (!pending.has(tab)) {
      const sectionUrl = new URL(url, window.location.origin);
      sectionUrl.searchParams.set('section_id', sectionId);
      pending.set(
        tab,
        fetch(sectionUrl)
          .then((response) => (response.ok ? response.text() : Promise.reject(new Error(response.status))))
          .then((html) => {
            const fetched = new DOMParser().parseFromString(html, 'text/html').querySelector('[search-alpha-view]');
            if (!fetched) throw new Error('no view in response');
            const view = document.importNode(fetched, true);
            // Lazy images aren't fetched while the view is hidden, so the first screenful would appear blank on
            // switching. Load those few now, in the background, so the tab opens with its pictures.
            view.querySelectorAll('img[loading="lazy"]').forEach((image, index) => {
              if (index < 8) image.loading = 'eager';
            });
            view.hidden = true;
            view.dataset.searchAlphaFetched = 'true';
            // After the original, so document-wide lookups in main.js keep finding the original's elements first
            firstView.parentNode.appendChild(view);
            views.set(tab, view);
            shareCounts();
            window.$th_refreshWishlistItems?.();
            searchAlphaRefreshSalePrices();
            return view;
          })
          .catch((error) => {
            pending.delete(tab);
            throw error;
          })
      );
    }
    return pending.get(tab);
  };

  const show = (tab) => {
    views.forEach((view, key) => {
      view.hidden = key !== tab;
    });
    document.documentElement.classList.remove(SWITCHING_CLASS);
    // Keep the tab bar in view if the shopper had scrolled down the results
    const bar = views.get(tab).querySelector('[search-alpha-tabs]');
    const headerHeight = document.querySelector('header.header')?.getBoundingClientRect().height || 0;
    const barTop = bar ? bar.getBoundingClientRect().top + window.scrollY - headerHeight - 12 : 0;
    if (window.scrollY > barTop) window.scrollTo({ top: Math.max(barTop, 0), behavior: 'auto' });
  };

  const switchTo = (link) => {
    const tab = tabOf(link.href);
    if (views.get(tab) && !views.get(tab).hidden) return;
    document.documentElement.classList.add(SWITCHING_CLASS);
    load(link.href)
      .then(() => {
        show(tab);
        window.history.pushState({ searchAlphaTab: tab }, '', link.href);
      })
      .catch(() => window.location.assign(link.href));
  };

  window.history.replaceState({ ...(window.history.state || {}), searchAlphaTab: firstView.dataset.searchTab }, '');
  window.addEventListener('popstate', (event) => {
    const tab = event.state?.searchAlphaTab;
    if (tab && views.has(tab)) show(tab);
    else window.location.reload();
  });

  // Back/forward cache restores the page as it was left; make sure no fade is stuck on
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) document.documentElement.classList.remove(SWITCHING_CLASS);
  });

  let touchStart = null;
  let handledByPointer = false;
  document.addEventListener('pointerdown', (event) => {
    const link = event.target instanceof Element && event.target.closest(TAB_SELECTOR);
    touchStart = link && event.pointerType !== 'mouse' ? { link, x: event.clientX, y: event.clientY } : null;
  });
  document.addEventListener('pointercancel', () => {
    touchStart = null;
  });
  document.addEventListener('pointerup', (event) => {
    if (!touchStart) return;
    const { link, x, y } = touchStart;
    touchStart = null;
    if (Math.hypot(event.clientX - x, event.clientY - y) > TAP_TOLERANCE) return;
    handledByPointer = true;
    switchTo(link);
  });

  document.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element)) return;

      const link = event.target.closest(TAB_SELECTOR);
      if (link) {
        // Modified clicks (new tab/window) keep their normal behaviour
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
        event.preventDefault();
        if (handledByPointer) {
          handledByPointer = false;
          return;
        }
        switchTo(link);
        return;
      }

      // A fetched Products view's filters and sort were never wired up; load the page properly for them
      const fetchedView = event.target.closest('[search-alpha-view][data-search-alpha-fetched]');
      if (fetchedView && event.target.closest(PAGE_LOAD_CONTROLS)) {
        event.preventDefault();
        event.stopPropagation();
        window.location.assign(window.location.href);
      }
    },
    true
  );

  // Fetch the other tab once the page has settled, so it's ready before the shopper reaches for it
  const otherTab = firstView.querySelector(`${TAB_SELECTOR}:not(.${ACTIVE_CLASS})`);
  if (otherTab) {
    const prefetch = () =>
      searchAlphaQuery
        .expandUrl(otherTab.getAttribute('href'))
        .then((href) => {
          otherTab.setAttribute('href', href);
          return load(otherTab.href);
        })
        .catch(() => {});
    if ('requestIdleCallback' in window) window.requestIdleCallback(prefetch, { timeout: 1500 });
    else window.setTimeout(prefetch, 300);
  }
})();

/**
 * Search results page bar (snippets/search-page-search-form.liquid): the round button also refocuses the field
 * (clearing it and its dropdown is handled by initAlphaSearch above).
 */
(function () {
  const clearButton = document.querySelector('[search-alpha-page-clear]');
  const input = clearButton?.closest('form')?.querySelector('[search-section-form-input]');
  if (!input) return;

  clearButton.addEventListener('click', () => input.focus());
})();

/**
 * Search results page: a blog tag filter only applies to the Blogs tab. If one ends up on the Products tab
 * (an old link, or switching tabs from a filtered Blogs page), reload the clean product search instead of
 * showing products filtered by a blog tag.
 */
(function () {
  const activeProductsTab = document.querySelector('[search-alpha-tabs] .search-alpha-tabs__tab--active[href*="type=product"]');
  if (!activeProductsTab) return;

  const query = new URLSearchParams(window.location.search).get('q') || '';
  if (query.includes('tag:')) window.location.replace(activeProductsTab.href);
})();

/**
 * Search results page: a product search that arrived with the shopper's unfinished word (a recent-search link, an
 * old bookmark) reloads once with it completed, so it finds what the dropdown and Enter find (see searchAlphaQuery).
 * The completed query is remembered for the tab, so a completion is never completed again in a loop.
 */
(function () {
  const activeProductsTab = document.querySelector('[search-alpha-tabs] .search-alpha-tabs__tab--active[href*="type=product"]');
  if (!activeProductsTab) return;

  const COMPLETED_KEY = 'searchAlphaCompletedQuery';
  const query = (new URLSearchParams(window.location.search).get('q') || '').trim();
  if (!query || query.includes('tag:')) return;

  try {
    if (window.sessionStorage.getItem(COMPLETED_KEY) === query) return;
  } catch (error) {
    // Storage blocked (private mode): carry on, the completion of a whole word returns it unchanged
  }

  searchAlphaQuery.expand(query).then((expanded) => {
    if (expanded === query) return;
    try {
      window.sessionStorage.setItem(COMPLETED_KEY, expanded);
    } catch (error) {
      // Storage blocked: the reload still happens
    }
    const url = new URL(window.location.href);
    url.searchParams.set('q', expanded);
    window.location.replace(url.href);
  });
})();

/**
 * Blogs tab pills (snippets/search-alpha-blog-tags.liquid): pill links end in #search-blogs. The browser's own
 * anchor jump runs before the theme's loader settles the page, so scroll there once the page has loaded,
 * below the fixed header. Then scroll the pill row so the active pill is in view.
 */
(function () {
  const blogs = document.getElementById('search-blogs');
  if (blogs && window.location.hash === '#search-blogs') {
    const scrollToBlogs = () => {
      const headerHeight = document.querySelector('header.header')?.getBoundingClientRect().height || 0;
      const top = blogs.getBoundingClientRect().top + window.scrollY - headerHeight;
      window.scrollTo({ top: Math.max(top, 0), behavior: 'auto' });
    };

    if (document.readyState === 'complete') {
      scrollToBlogs();
    } else {
      window.addEventListener('load', scrollToBlogs, { once: true });
    }
  }

  const activePill = document.querySelector('.search-alpha-blog-tags__link--active');
  const list = activePill?.closest('.search-alpha-blog-tags__list');
  if (!list) return;

  const pillRect = activePill.getBoundingClientRect();
  const listRect = list.getBoundingClientRect();
  list.scrollLeft += pillRect.left + pillRect.width / 2 - (listRect.left + listRect.width / 2);
})();

/**
 * Blogs tab pills + pagination: swap the blogs section in place instead of reloading the page, so the page
 * doesn't jump to the top and back. Only #search-blogs is fetched (Section Rendering API on the search section)
 * and replaced; the URL is updated so filters stay shareable and Back/Forward step through them.
 * Without JS the links still work as normal page loads.
 */
(function () {
  const BLOGS_ID = 'search-blogs';
  const LOADING_CLASS = 'search-alpha-blogs--loading';

  const getBlogs = () => document.getElementById(BLOGS_ID);
  const section = getBlogs()?.closest('.shopify-section');
  if (!section) return;

  const sectionId = section.id.replace('shopify-section-', '');
  let abortController = null;

  const centreActivePill = (behavior) => {
    const activePill = getBlogs()?.querySelector('.search-alpha-blog-tags__link--active');
    const list = activePill?.closest('.search-alpha-blog-tags__list');
    if (!list) return;

    const pillRect = activePill.getBoundingClientRect();
    const listRect = list.getBoundingClientRect();
    list.scrollBy({ left: pillRect.left + pillRect.width / 2 - (listRect.left + listRect.width / 2), behavior });
  };

  const load = (href, { push = true, scrollToTop = false } = {}) => {
    const url = new URL(href, window.location.origin);
    url.hash = '';
    const blogs = getBlogs();
    const previousPillScroll = blogs.querySelector('.search-alpha-blog-tags__list')?.scrollLeft || 0;

    abortController?.abort();
    abortController = new AbortController();
    blogs.classList.add(LOADING_CLASS);

    const sectionUrl = new URL(url);
    sectionUrl.searchParams.set('section_id', sectionId);

    fetch(sectionUrl, { signal: abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const nextBlogs = doc.getElementById(BLOGS_ID);
        if (!nextBlogs) throw new Error('No blogs section in response');

        // Swap in faded, then fade up on the next frame.
        nextBlogs.classList.add(LOADING_CLASS);
        blogs.replaceWith(nextBlogs);
        setTimeout(() => nextBlogs.classList.remove(LOADING_CLASS), 30);

        // Keep the pill row where it was, then glide the newly active pill into view.
        const nextList = nextBlogs.querySelector('.search-alpha-blog-tags__list');
        if (nextList) nextList.scrollLeft = previousPillScroll;
        centreActivePill('smooth');

        // A filter with no blogs renders no header, so show 0 rather than keep the previous filter's count.
        const nextCount = doc.querySelector('[search-header-count]');
        const count = document.querySelector('[search-header-count]');
        if (count) count.innerHTML = nextCount ? nextCount.innerHTML : 'Showing <span>0 results</span>';

        if (push) window.history.pushState({ searchAlphaBlogs: true }, '', url);
        if (scrollToTop) {
          const headerHeight = document.querySelector('header.header')?.getBoundingClientRect().height || 0;
          const top = nextBlogs.getBoundingClientRect().top + window.scrollY - headerHeight;
          window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        console.error('[search-alpha]', error);
        window.location.href = url.toString();
      });
  };

  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (!(event.target instanceof Element)) return;

    const pill = event.target.closest(`#${BLOGS_ID} [search-alpha-blog-tag]`);
    const pageLink = event.target.closest(`#${BLOGS_ID} .collection-listing__pagination a[href]`);
    if (!pill && !pageLink) return;

    event.preventDefault();
    load((pill || pageLink).getAttribute('href'), { scrollToTop: Boolean(pageLink) });
  });

  // Back/Forward between filters swaps the section again instead of leaving the page stale.
  window.addEventListener('popstate', () => {
    if (new URLSearchParams(window.location.search).get('type') !== 'article') return;
    load(window.location.href, { push: false });
  });
})();

/**
 * Search results page offset: main.css pushes the page down by a fixed header height, which assumes a one-line
 * announcement bar. A longer message wraps to two lines on phones and the fixed header then covers the search
 * box, so use the real header height instead.
 *
 * Always the header's fully open height (header row + the announcement bar's full content), never its current
 * height: the bar animates shut on scroll and back open at the top, and following that live made the page jump.
 */
(function () {
  const header = document.querySelector('header.header');
  if (!header || !document.querySelector('.search-alpha-page-search')) return;

  const bar = header.querySelector('.header__announcement-bar');
  const BAR_MAX_HEIGHT = 128; // main.css .header__announcement-bar { max-height: 8rem }
  let applied = 0;

  const measure = () => {
    let height = header.offsetHeight;
    if (bar) height = height - bar.offsetHeight + Math.min(bar.scrollHeight, BAR_MAX_HEIGHT);
    height = Math.ceil(height);
    if (height === applied) return;
    applied = height;
    document.documentElement.style.setProperty('--search-alpha-header-offset', `${height}px`);
  };

  measure();
  window.addEventListener('load', measure);
  window.addEventListener('resize', measure);
  // Message or font changes; collapse/expand on scroll leaves the result unchanged, so it causes no movement
  new ResizeObserver(measure).observe(bar || header);
})();
